import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import stripeService from '../../services/stripeService';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const companyId = searchParams?.get('company_id');
  const sessionId = searchParams?.get('session_id');

  useEffect(() => {
    const loadSubscription = async () => {
      if (companyId) {
        try {
          const sub = await stripeService?.getSubscriptionStatus(companyId);
          setSubscription(sub);
        } catch (err) {
          console.error('Error loading subscription:', err);
        }
      }
      setLoading(false);
    };

    loadSubscription();

    const timer = setTimeout(() => {
      navigate('/workshop-dashboard');
    }, 6000);

    return () => clearTimeout(timer);
  }, [companyId, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-lg w-full text-center">
        {/* Success Icon */}
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" className="w-12 h-12 text-green-600" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-3">¡Suscripción Activada!</h1>
        <p className="text-gray-600 mb-6">
          Tu suscripción ha sido procesada exitosamente. Bienvenido a GearControl.
        </p>

        {/* Subscription Details */}
        {!loading && subscription && (
          <div className="bg-gray-50 rounded-xl p-5 mb-6 text-left">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Icon name="CreditCard" className="w-4 h-4 text-blue-500" />
              Detalles de tu Plan
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Plan</span>
                <span className="font-medium text-gray-800">
                  {subscription?.subscription_plans?.plan_name ?? 'Plan Activo'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Estado</span>
                <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                  <Icon name="CheckCircle" className="w-3.5 h-3.5" />
                  Activo
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Ciclo de Facturación</span>
                <span className="font-medium text-gray-800 capitalize">
                  {subscription?.billing_cycle ?? 'Mensual'}
                </span>
              </div>
              {subscription?.current_period_end && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Próxima Renovación</span>
                  <span className="font-medium text-gray-800">
                    {new Date(subscription.current_period_end)?.toLocaleDateString('es-MX', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
            <Icon name="Loader2" className="w-5 h-5 animate-spin" />
            <span className="text-sm">Verificando suscripción...</span>
          </div>
        )}

        {/* Features unlocked */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <p className="text-sm font-medium text-blue-800 mb-2">✨ Acceso completo habilitado</p>
          <div className="grid grid-cols-2 gap-1.5">
            {['Recepción de vehículos', 'Diagnóstico técnico', 'Cotizaciones', 'Ventas', 'Reportes avanzados', 'Automatización']?.map(feature => (
              <div key={feature} className="flex items-center gap-1.5 text-xs text-blue-700">
                <Icon name="Check" className="w-3 h-3 text-blue-500 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </div>

        {/* Redirect notice */}
        <div className="flex items-center justify-center gap-2 text-blue-600">
          <Icon name="Loader2" className="w-5 h-5 animate-spin" />
          <span className="text-sm">Redirigiendo al dashboard en unos segundos...</span>
        </div>

        <button
          onClick={() => navigate('/workshop-dashboard')}
          className="mt-4 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Icon name="LayoutDashboard" className="w-4 h-4" />
          Ir al Dashboard ahora
        </button>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
